
import React, { useRef, useState } from "react";

import {
    Box,
    Button,
    Alert,
    AlertTitle,
    LinearProgress,
    Stack,
    Chip,
    Card,
    CardContent,
    Divider,
    Paper,
    Typography,
} from "@mui/material";

import {
    CloudUpload,
    Description,
    CheckCircle,
    Delete,
   
} from "@mui/icons-material";


import PageHeader from "../../../utils/PageHeader";

import StorageIcon from "@mui/icons-material/Storage";

import { api } from "../../../component/constent";


const IMMISDataImport = () => {
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    // ========================================================
    // FILE SELECT
    // ========================================================

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];

        setMessage("");
        setError("");

        if (!selectedFile) {
            setFile(null);
            return;
        }

        if (
            !selectedFile.name
                .toLowerCase()
                .endsWith(".csv")
        ) {
            setError(
                "Invalid file format. Please select a CSV file."
            );

            e.target.value = "";
            setFile(null);

            return;
        }

        setFile(selectedFile);
    };

    // ========================================================
    // REMOVE FILE
    // ========================================================

    const handleRemoveFile = () => {
        setFile(null);
        setMessage("");
        setError("");

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // ========================================================
    // IMPORT
    // ========================================================

    const handleImport = async () => {
        setMessage("");
        setError("");

        if (!file) {
            setError(
                "Please select a CSV file before importing."
            );

            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to import "${file.name}"?\n\n` +
            "The CSV file will be validated before the data is saved."
        );

        if (!confirmed) {
            return;
        }

        const formData = new FormData();

        formData.append("file", file);

        try {
            setLoading(true);

            const response = await api.post(
                "/entry/import",
                formData,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data",
                    },
                }
            );

            if (response.data.success) {
                setMessage(
                    `${response.data.message} ` +
                    `Total rows imported: ${response.data.importedRows}`
                );

                setFile(null);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        } catch (err) {
            console.error(
                "IMMIS import error:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to import the CSV file. Please verify the file and try again."
            );
        } finally {
            setLoading(false);
        }
    };

    // ========================================================
    // UI
    // ========================================================

    return (
        <Box
            sx={{
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            {/* =================================================
                PROFESSIONAL HEADER
            ================================================= */}

           <PageHeader
  title="IMMIS Data Import"
  subtitle="Import validated IMMIS data from a CSV file into the system."
  icon={StorageIcon}
  statusLabel="Import Format"
  statusValue="CSV File"
  statusChip={{
    label: loading
      ? "Importing..."
      : file
      ? "File Selected"
      : "Ready",
    color: loading
      ? "warning"
      : file
      ? "primary"
      : "success",
  }}
 // showRefresh
 // onRefresh={handleRefresh}
  refreshing={loading}
/>

            {/* =================================================
                MAIN IMPORT CARD
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                }}
            >
                {/* Section Header */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            md: 3,
                        },
                        py: 2,
                        backgroundColor: "grey.50",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                    >
                        <CloudUpload
                            color="primary"
                            sx={{
                                fontSize: 28,
                            }}
                        />

                        <Box>
                            <Typography
                                variant="subtitle1"
                                fontWeight={700}
                            >
                                Import IMMIS Data
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Select the IMMIS CSV file to
                                upload and import into the system.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <CardContent
                    sx={{
                        p: {
                            xs: 2,
                            sm: 3,
                            md: 4,
                        },
                    }}
                >
                    {/* =================================================
                        FILE SELECT AREA
                    ================================================= */}

                    <Box
                        sx={{
                            border: "2px dashed",
                            borderColor: file
                                ? "success.main"
                                : "divider",
                            borderRadius: 3,
                            p: {
                                xs: 2.5,
                                sm: 4,
                            },
                            textAlign: "center",
                            bgcolor: file
                                ? "rgba(46,125,50,0.03)"
                                : "background.default",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                mx: "auto",
                                mb: 1.5,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: file
                                    ? "success.light"
                                    : "primary.light",
                            }}
                        >
                            <Description
                                sx={{
                                    fontSize: 38,
                                    color: file
                                        ? "success.dark"
                                        : "primary.dark",
                                }}
                            />
                        </Box>

                        <Typography
                            variant="h6"
                            fontWeight={700}
                        >
                            Select IMMIS CSV File
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                mb: 2.5,
                            }}
                        >
                            Only CSV files are accepted
                        </Typography>

                        <input
                            ref={fileInputRef}
                            id="immisCsvFile"
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleFileChange}
                            disabled={loading}
                            hidden
                        />

                        <Button
                            component="label"
                            htmlFor="immisCsvFile"
                            variant="outlined"
                            size="large"
                            startIcon={<CloudUpload />}
                            disabled={loading}
                            sx={{
                                minWidth: 190,
                                height: 46,
                                borderRadius: 2,
                                fontWeight: 700,
                                textTransform: "none",
                            }}
                        >
                            Choose CSV File
                        </Button>
                    </Box>

                    {/* =================================================
                        SELECTED FILE
                    ================================================= */}

                    {file && (
                        <Card
                            variant="outlined"
                            sx={{
                                mt: 2,
                                borderRadius: 2.5,
                                borderColor: "success.main",
                                backgroundColor:
                                    "rgba(46,125,50,0.02)",
                            }}
                        >
                            <CardContent
                                sx={{
                                    py: 1.75,
                                    "&:last-child": {
                                        pb: 1.75,
                                    },
                                }}
                            >
                                <Stack
                                    direction={{
                                        xs: "column",
                                        sm: "row",
                                    }}
                                    spacing={1.5}
                                    alignItems={{
                                        xs: "flex-start",
                                        sm: "center",
                                    }}
                                    justifyContent="space-between"
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                    >
                                        <Box
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent:
                                                    "center",
                                                backgroundColor:
                                                    "success.light",
                                            }}
                                        >
                                            <Description
                                                color="success"
                                            />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                sx={{
                                                    wordBreak:
                                                        "break-word",
                                                }}
                                            >
                                                {file.name}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {(
                                                    file.size /
                                                    1024
                                                ).toFixed(1)}{" "}
                                                KB
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Chip
                                            label="CSV"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        />

                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={
                                                <Delete />
                                            }
                                            onClick={
                                                handleRemoveFile
                                            }
                                            disabled={loading}
                                            sx={{
                                                fontWeight: 600,
                                                textTransform:
                                                    "none",
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* =================================================
                        LOADING
                    ================================================= */}

                    {loading && (
                        <Box sx={{ mt: 3 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{
                                    mb: 0.75,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    Importing CSV data...
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Please wait
                                </Typography>
                            </Stack>

                            <LinearProgress
                                sx={{
                                    borderRadius: 5,
                                    height: 6,
                                }}
                            />
                        </Box>
                    )}

                    {/* =================================================
                        SUCCESS MESSAGE
                    ================================================= */}

                    {message && (
                        <Alert
                            severity="success"
                            icon={<CheckCircle />}
                            sx={{
                                mt: 3,
                                borderRadius: 2,
                            }}
                            onClose={() =>
                                setMessage("")
                            }
                        >
                            <AlertTitle>
                                Import Successful
                            </AlertTitle>

                            {message}
                        </Alert>
                    )}

                    {/* =================================================
                        ERROR MESSAGE
                    ================================================= */}

                    {error && (
                        <Alert
                            severity="error"
                            icon={<Delete />}
                            sx={{
                                mt: 3,
                                borderRadius: 2,
                            }}
                            onClose={() =>
                                setError("")
                            }
                        >
                            <AlertTitle>
                                Import Failed
                            </AlertTitle>

                            {error}
                        </Alert>
                    )}

                    {/* =================================================
                        IMPORT ACTION
                    ================================================= */}

                    <Box
                        sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: {
                                xs: "stretch",
                                sm: "flex-end",
                            },
                        }}
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<CloudUpload />}
                            onClick={handleImport}
                            disabled={
                                loading ||
                                !file
                            }
                            sx={{
                                minWidth: {
                                    sm: 190,
                                },
                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                                height: 48,
                                fontWeight: 700,
                                borderRadius: 2,
                                textTransform: "none",
                                boxShadow:
                                    "0 5px 14px rgba(25,118,210,0.22)",
                            }}
                        >
                            {loading
                                ? "Importing..."
                                : "Import CSV"}
                        </Button>
                    </Box>

                    <Divider
                        sx={{
                            mt: 3,
                            mb: 3,
                        }}
                    />

                    {/* =================================================
                        INFORMATION
                    ================================================= */}

                    <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                        }}
                    >
                        <AlertTitle>
                            Before You Import
                        </AlertTitle>

                        Please make sure the selected file is
                        the correct IMMIS CSV file. The file will
                        be validated before any data is saved.
                    </Alert>
                </CardContent>
            </Paper>
        </Box>
    );
};

export default IMMISDataImport;

